import { Box, Button } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { onlineClassesPath } from "@outschool/routes";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  PageWidthSection,
  TrackingProperties,
} from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";
import { validate as isUuid } from "uuid";

import {
  SIMILARITY_ALGORITHMS,
  SimilarityAlgorithms,
} from "../graphql/SimilarListingQueries";
import ListingCarousel from "./ListingCarousel";

export type SimilarListingsProps = {
  activity: any;
  title: string;
  carouselRowNum: number;
  currentUserIsEnrolled?: boolean;
  algorithm?: SimilarityAlgorithms;
  showSubjectSearch?: boolean;
  backgroundColor?: string;
  batchKey?: string;
  miniTiles?: boolean;
  trackingProperties?: TrackingProperties;
};

export default function SimilarListingsCarousel({
  activity,
  title,
  carouselRowNum,
  currentUserIsEnrolled = false,
  algorithm = "popular",
  showSubjectSearch = false,
  backgroundColor = undefined,
  batchKey = "similarListings",
  miniTiles = false,
  trackingProperties,
}: SimilarListingsProps) {
  const Link = useLinkComponent();
  const { query } = SIMILARITY_ALGORITHMS[algorithm];
  const { data, loading } = useQueryWithPreviousData(query, {
    skip: !activity || !isUuid(activity.uid),
    variables: {
      activityUid: activity && activity.uid,
      skipRating: false,
      withRecentReviews: false,
      limit: miniTiles ? 12 : null,
    },
    context: { doNotRetry: true, batchKey },
  });
  const similarListings = data?.listing?.similarListings[algorithm] ?? [];
  const subject = activity.subject;
  const { t } = useTranslation(
    "ui-components-website\\src\\Listings\\Carousels\\SimilarListingsCarousel"
  );

  if (!loading && (!similarListings || !Boolean(similarListings.length))) {
    // if Popular MBA has no results, fallback to basic popular model
    if (algorithm === "popularMBA") {
      return (
        <SimilarListingsCarousel
          activity={activity}
          title={title}
          carouselRowNum={carouselRowNum}
          currentUserIsEnrolled={currentUserIsEnrolled}
          algorithm={"popularElasticsearch"}
          showSubjectSearch={showSubjectSearch}
          backgroundColor={backgroundColor}
          batchKey={batchKey}
          trackingProperties={trackingProperties}
        />
      );
    } else {
      return null;
    }
  }

  return (
    <PageWidthSection sx={{ backgroundColor }}>
      <ListingCarousel
        sx={{ padding: 0, backgroundColor }}
        listings={similarListings}
        title={title}
        trackingLabel={"listing_card"}
        trackingProperties={{
          carousel_type: "similar_listings",
          algorithm,
          currentUserIsEnrolled,
          ...trackingProperties,
        }}
        carouselRowNum={carouselRowNum}
        leftAlign
        headingSx={{ textAlign: "left" }}
      >
        {showSubjectSearch && (
          <Box
            flex
            sx={{
              marginTop: "2em",
              justifyContent: "center",
            }}
          >
            {/* @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message */}
            <Button component={Link} to={onlineClassesPath(subject)}>
              {t('More "{{subject}}" classes', { subject })}
            </Button>
          </Box>
        )}
      </ListingCarousel>
    </PageWidthSection>
  );
}
